
import { Component, Output, EventEmitter, HostListener, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef,  OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient, } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { CreateProjectService } from '../services/create-project.service';
import { renderAsync } from 'docx-preview';
import * as XLSX from 'xlsx';
import { RoleViewService } from '../services/role-view.service';
import { UploadConfirmationDialogComponent } from '../components/bulk-upload-confirmation-dialog/bulk-upload-confirmation-dialog.component';
import { CommonModule } from '@angular/common';
import { DocumentTypeIconComponent } from './document-type-icon/document-type-icon.component';
import { CopySourceDocumentsComponent } from './copy-source-documents/copy-source-documents.component';
import { AttachmentFileService } from './attachment-file.service';

type PreviewType = 'pdf' | 'docx' | 'xls' | 'image' | 'unsupported' | null;

@Component({
  selector: 'app-select-attachments',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, DocumentTypeIconComponent, CopySourceDocumentsComponent],
  templateUrl: './select-attachments.component.html',
  styleUrl: './select-attachments.component.css'
})
export class SelectAttachmentsComponent implements OnInit, OnDestroy {
  // Exposed so the template can call DocumentTypeIconComponent's static formatting helpers directly.
  protected readonly DocumentTypeIconComponent = DocumentTypeIconComponent;

  constructor(
    public  createProjectService: CreateProjectService,
    public roleviewservice: RoleViewService,
    private readonly cdr: ChangeDetectorRef,
    private readonly sanitizer: DomSanitizer,
    private readonly http: HttpClient,
    private readonly dialog: MatDialog,
    private readonly attachmentFileService: AttachmentFileService
  ) {
    this.getScreenSize();
  }

  @ViewChild('docxContainer') docxContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('webviewerContainer', { static: false }) webviewerContainerRef!: ElementRef<HTMLDivElement>;

  private readonly FAILED_TO_FETCH_FILE = 'Failed to fetch file';

  isPreviewOpen = false;
  previewTitle = '';
  previewType: PreviewType = null;
  previewUrl: SafeResourceUrl | null = null;
  excelHtml = '';
  workbookCache: any = null;
  sheetNames: string[] = [];
  selectedSheetIndex = 0;
  private _previewHistoryState = false;
  private _ignoreNextPop = false;
  private _onPopState: ((ev: PopStateEvent) => void) | null = null;
  workDayId: any;
  projectId: any;
  collectionId: any;
  dfuId?: number;
  projectName: any;
  userName: any;
  selectedDepartmentIds: any[] = [];
  isUploadEnabled = false;
  allAvailableDepartments: any[] = [];
  @Output() selectedCountChange: EventEmitter<number> = new EventEmitter<number>();
  files: Array<{
    id: number;
    name: string;
    size: number;
    date: string;
    type: string;
    fromCollection?: boolean;
    checked?: boolean;
  }> = [];
  cardHeightPx = 0;
  scrHeight: any;
  @HostListener('window:resize', ['$event'])
  getScreenSize(_event?:any) {
    this.scrHeight = `${window.innerHeight - 260}px`;
  }

  userUploads: Array<any> = [];
  uploadErrorMessage: string | null = null;
  private _objectUrls: string[] = [];


  ngOnInit() {
    this.getUserInfo();
    this.projectId = this.createProjectService.getProjectId();
    this.projectName = this.createProjectService.getSelectedProject().projectName;
    this.collectionId = this.createProjectService.getSelectedCollection().collection_id;

    // Get selected department IDs from service and populate available departments
    const selectedDept = this.createProjectService.getSelectedDepartment();
    if (selectedDept?.selectedDepartments && selectedDept.selectedDepartments.length > 0) {
      // Build list of all available departments from selected departments
      this.allAvailableDepartments = selectedDept.selectedDepartments.map((d: any) => ({
        id: String(d.id),
        name: d.name || '',
        selected: true
      }));
      
      // Preserve selected department IDs from service if available
      const preservedDeptIds = this.createProjectService.getSelectedAttachmentDepartmentIds;
      if (preservedDeptIds && preservedDeptIds.length > 0) {
        this.selectedDepartmentIds = preservedDeptIds;
      } else {
        this.selectedDepartmentIds = selectedDept.selectedDepartments.map((d: any) => String(d.id));
      }
      this.isUploadEnabled = true;
    } else {
      this.allAvailableDepartments = [];
      this.selectedDepartmentIds = [];
      this.isUploadEnabled = false;
    }

    this.refreshProjectUploads();
    this.fetchDocument();
  }
  getUserInfo() {
    this.workDayId = this.roleviewservice.getWorkdayId() || '';
    this.userName = this.roleviewservice.getUserName();
  }

  public updateSelectedDepartmentIds(): void {
    const selectedDept = this.createProjectService.getSelectedDepartment();
    if (selectedDept?.selectedDepartments && selectedDept.selectedDepartments.length > 0) {
      this.allAvailableDepartments = selectedDept.selectedDepartments.map((d: any) => ({
        id: String(d.id),
        name: d.name || '',
        selected: true
      }));
      
      // Preserve selected department IDs from service if available
      const preservedDeptIds = this.createProjectService.getSelectedAttachmentDepartmentIds;
      if (preservedDeptIds && preservedDeptIds.length > 0) {
        this.selectedDepartmentIds = preservedDeptIds;
      } else {
        this.selectedDepartmentIds = selectedDept.selectedDepartments.map((d: any) => String(d.id));
      }
      this.isUploadEnabled = true;
    } else {
      this.allAvailableDepartments = [];
      this.selectedDepartmentIds = [];
      this.isUploadEnabled = false;
    }
    console.info("Department IDs updated:", this.selectedDepartmentIds);
    this.cdr.detectChanges();
  }

  /**
   * Check if a department is selected
   */
  isSelectedDepartment(deptId: string): boolean {
    return this.selectedDepartmentIds.includes(String(deptId));
  }

  /**
   * Toggle department selection
   */
  toggleDepartmentSelection(dept: any): void {
    const deptId = String(dept.id);
    const index = this.selectedDepartmentIds.indexOf(deptId);

    if (index > -1) {
      // Remove if already selected
      this.selectedDepartmentIds.splice(index, 1);
    } else {
      // Add if not selected
      this.selectedDepartmentIds.push(deptId);
    }

    // Update upload enabled status
    this.isUploadEnabled = this.selectedDepartmentIds.length > 0;

    // Preserve selected department IDs in service
    this.createProjectService.setSelectedAttachmentDepartmentIds = this.selectedDepartmentIds;

    console.info("Department selection toggled. Selected IDs:", this.selectedDepartmentIds);
    this.cdr.detectChanges();
  }

  fetchDocument() {
    this.createProjectService.fetchDocument(this.collectionId).subscribe(res => {

      this.files = res.payload.filter((f:any)=> !f.is_deleted);
      this.applyUploadedSelectionStateToCollectionFiles();
      console.info("fetchdoc", this.files)
    })
  }

  get collectionCount(): number {
    return this.files.length;
  }

  get selectedCount(): number {
    const all = [...this.files, ...this.userUploads] as any[];
    return all.filter(f => f.checked).length;
  }

  get totalSize(): string {
    const total = this.files.reduce((s, f) => s + (f.size || 0), 0) + this.userUploads.reduce((s, u: any) => s + (u.size || 0), 0);
    return DocumentTypeIconComponent.formatBytes(total);
  }

  isAllSelected(): boolean {
    // Check if all collection files are selected
    return this.files.length > 0 && this.files.every(f => f.checked);
  }

  isAllCollectionSelected(): boolean {
    const coll = this.files.filter(f => f.fromCollection);
    return coll.length > 0 && coll.every(f => f.checked);
  }

  toggleAll() {
    const val = !this.isAllSelected();
    
    // If selecting all files, check for duplicates first then show confirmation dialog
    if (val) {
      const uploadedFileNames = new Set(
        this.userUploads.map((u: any) => (u.file_name || u.name || '').toLowerCase())
      );
      const duplicates = this.files
        .map(f => (f as any).file_name)
        .filter(name => uploadedFileNames.has((name || '').toLowerCase()));

      if (duplicates.length > 0) {
        this.dialog.open(UploadConfirmationDialogComponent, {
          width: '500px',
          data: {
            warningOnly: true,
            title: 'Duplicate Files Detected',
            message: `The following file(s) have already been uploaded. Please remove the existing file(s) and upload again:\n\n${duplicates.join('\n')}`,
            fileNames: duplicates
          }
        });
        return;
      }

      const selectedFileNames = this.files.map(f => (f as any).file_name);
      
      const dialogRef = this.dialog.open(UploadConfirmationDialogComponent, {
        width: '500px',
        data: {
          fileNames: selectedFileNames,
          departments: this.selectedDepartmentIds,
          isBulk: true
        }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'yes') {
          // User confirmed, proceed with upload
          this.files.forEach(f => f.checked = true);
          this.uploadSelectedCollectionFiles();
          this.selectedCountChange.emit(this.selectedCount);
          this.updateSelectedFilesInService();
        }
        // If 'no', do nothing - files remain unchecked
      });
      
      return;
    }
    
    // If deselecting all files, just uncheck them without confirmation
    this.files.forEach(f => f.checked = val);
    this.selectedCountChange.emit(this.selectedCount);
    this.updateSelectedFilesInService();
  }

  private uploadSelectedCollectionFiles(): void {
    const selectedFiles = this.files.filter(f => f.checked);
    if (selectedFiles.length === 0) {
      return;
    }

    // Upload files sequentially (one by one)
    this.uploadFilesSequentially(selectedFiles, 0);
  }

  private uploadFilesSequentially(files: any[], index: number): void {
    if (index >= files.length) {
      // All files uploaded, refresh the list
      console.info('All files uploaded successfully');
      this.refreshProjectUploads();
      return;
    }

    const file = files[index];
    const uploadPayload: any = {
      projectId: this.projectId,
      projectName: this.projectName,
      departmentIds: this.selectedDepartmentIds,
      fileName: file.file_name,
      userName: this.userName,
      uploadedBy: this.userName,
      isCollectionDoc: true,
      collectionFilePath: file.file_path,
      contentType: DocumentTypeIconComponent.getMimeType(file.file_name)
    };

    console.info(`Uploading file ${index + 1} of ${files.length}: ${file.file_name}`);

    this.createProjectService.uploadDocument(this.workDayId, uploadPayload).subscribe({
      next: (res) => {
        console.info(`File ${index + 1} uploaded successfully:`, res);
        // Upload next file
        this.uploadFilesSequentially(files, index + 1);
      },
      error: (error) => {
        console.trace(`Error uploading file ${index + 1}:`, error);
        // Continue with next file even if one fails
        this.uploadFilesSequentially(files, index + 1);
      }
    });
  }

  toggleCollectionSelectAll() {
    const val = !this.isAllCollectionSelected();
    this.files.forEach(f => {
      if (f.fromCollection) {
        f.checked = val;
      }
    });
    this.selectedCountChange.emit(this.selectedCount);
    this.updateSelectedFilesInService();
  }

  toggleFileSelection(file: any, skipConfirm = false) {
    file.checked = !file.checked;
    
    // Only proceed if file is checked
    if (file.checked && !skipConfirm) {
      // Show confirmation dialog for individual file selection
      const dialogRef = this.dialog.open(UploadConfirmationDialogComponent, {
        width: '500px',
        data: {
          fileNames: [file.file_name],
          departments: this.selectedDepartmentIds,
          isBulk: false
        }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'yes') {
          // User confirmed, proceed with upload
          this.onUploadCollectionFile(file);
        } else {
          file.checked = false;
        }
        
        this.selectedCountChange.emit(this.selectedCount);
        this.updateSelectedFilesInService();
      });
      
      return;
    } else if (file.checked && skipConfirm) {
      this.onUploadCollectionFile(file);
    }
    
    this.selectedCountChange.emit(this.selectedCount);
    this.updateSelectedFilesInService();
  }

  onUploadCollectionFile(f:any) {
        const uploadPayload: any = {
          projectId: this.projectId,
          projectName: this.projectName,
          departmentIds: this.selectedDepartmentIds,
          fileName: f.file_name,
          userName: this.userName,
          uploadedBy: this.userName,
          isCollectionDoc: true,
          collectionFilePath : f.file_path,
          contentType: DocumentTypeIconComponent.getMimeType(f.file_name)
        }


        // First call: upload binary to storage
        this.createProjectService.uploadDocument(this.workDayId, uploadPayload).subscribe(
          (res) => {
            console.info("upload", res);
            this.refreshProjectUploads();
          }
        );
      
    }
  deselectAll() {
    this.files.forEach(f => f.checked = false);
    this.userUploads.forEach(u => u.checked = false);
    this.selectedCountChange.emit(this.selectedCount);
    this.updateSelectedFilesInService();
  }

  downloadSelected() {
    let selected = [...this.files, ...this.userUploads].filter((f: any) => f.checked);
    if (selected.length === 0 && this.userUploads.length > 0) {
      selected = [...this.userUploads];
    }
    console.info('downloadSelected', selected.map((s: any) => s.name));
    selected.forEach((s: any) => {
      if (s.objectUrl) {
        const a = document.createElement('a');
        a.href = s.objectUrl;
        a.download = s.name || 'download';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        console.info('Bulk download: file requires server download (collection file):', s.name);
      }
    });
  }

  removeSelected() {
    const all = [...this.files, ...this.userUploads];
    const anyChecked = all.some((f: any) => f.checked);
    if (!anyChecked) {
      return;
    }
    all.forEach((f: any) => {
      if (f.checked) 
      {
        f.checked = false;
      }
    });
    this.files = this.files.map(f => ({ ...f }));
    this.userUploads = this.userUploads.map(u => ({ ...u }));
    console.info('cleared selection for checked items');
    this.selectedCountChange.emit(this.selectedCount);
  }

  onBrowseFiles(event: Event) {

    // Validate that departments are selected
    if (!this.isUploadEnabled || this.selectedDepartmentIds.length === 0) {
      this.showUploadError('Please select departments before uploading files.');
      return;
    }

    const input = event.target as HTMLInputElement;
    if (!input.files)
    {
       return;
    }
    for (let i = 0; i < input.files.length; i++) {
      const f = input.files.item(i);
      if (!f) 
        {
          continue;
        }

      // Enforce allowed content types
      if (!DocumentTypeIconComponent.isAllowedFileType(f)) {
        console.info('Unsupported file type blocked:', f.name, f.type);
        this.showUploadError(`Unsupported file type for "${f.name}". Allowed: PDF, DOCX, XLSX, PNG, JPG, JPEG, CSV, XLSM`);
        continue;
      }

      const already = [...this.files, ...this.userUploads].some(x => String(x.name).toLowerCase() === String(f.name).toLowerCase() && Number(x.size) === Number(f.size));
      if (already) {
        console.info('Duplicate upload prevented for', f.name);
        this.showUploadError(`File "${f.name}" already uploaded`);
        continue;
      }

      this.fileToBase64(f).then(base64 => {
        const uploadPayload: any = {
          projectId: this.projectId,
          projectName: this.projectName,
          departmentIds: this.selectedDepartmentIds,
          fileName: f.name,
          contentType: f.type || DocumentTypeIconComponent.getMimeType(f.name),
          userName: this.userName,
          fileBuffer: base64,
          uploadedBy: this.userName,
        }


        // First call: upload binary to storage
        this.createProjectService.uploadDocument(this.workDayId, uploadPayload).subscribe(
          (res) => {
            console.info("upload", res);
            this.refreshProjectUploads();
          }
        );
      }
      )
    }

    input.value = '';
    this.selectedCountChange.emit(this.selectedCount);
    this.updateSelectedFilesInService();
  }

  download(file: any) {
    const id = file.file_versioning_id;
    if (id) {
      this.createProjectService.readDocument(this.workDayId, id).subscribe((res: any) => {
        this.attachmentFileService.downloadFromResponse(res, file.file_name, DocumentTypeIconComponent.MIME_PDF);
      });
      return;
    }

    console.info('Download not implemented for this file object:', file);
  }

  private _resolveUploadedFileUrl(file: any, id: any, name: string): Promise<string | null> {
    if (!id) {
      return Promise.resolve(file.serverUrl || file.remoteUrl || file.objectUrl || null);
    }
    return this.attachmentFileService
      .resolveFileUrl(this.createProjectService.readDocument(this.workDayId, id), name, url => this._objectUrls.push(url))
      .catch(err => {
        console.trace(this.FAILED_TO_FETCH_FILE, err);
        return null;
      });
  }

  private async _previewImage(file: any, getUrl: () => Promise<string | null>): Promise<void> {
    this.previewTitle = file.file_name;
    this.isPreviewOpen = true;
    this._addPreviewHistoryEntry();
    this.previewType = 'image';
    const url = await getUrl();
   if (url) { this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url); return; } // NOSONAR
    if (file.file) {
      const obj = URL.createObjectURL(file.file);
      this._objectUrls.push(obj);
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(obj); // NOSONAR
      return;
    }
    alert('Image preview requires a reachable URL; please download the file to view.');
  }

  async preview(file: any) {
    if (!file) { return; }

    const name = (file.file_name || '').toLowerCase();
    const id = file.file_versioning_id;
    const getUrl = () => this._resolveUploadedFileUrl(file, id, name);

    if (name.endsWith('.pdf')) { await this.attachmentFileService.openPdf(getUrl, file, url => this._objectUrls.push(url)); return; }

    const isImage = name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') ||
      (file.type && String(file.type).toLowerCase().startsWith('image/'));
    if (isImage) { await this._previewImage(file, getUrl); return; }

    this.previewTitle = file.file_name;
    this.isPreviewOpen = true;
    this._addPreviewHistoryEntry();

    if (name.endsWith('.docx')) {
      this.previewType = 'docx';
      this.previewUrl = null;
      const arrayBuffer = await this.attachmentFileService.fetchDocxBuffer(file, getUrl);
      if (arrayBuffer) {
        this.docxContainerRef.nativeElement.innerHTML = '';
        await renderAsync(arrayBuffer, this.docxContainerRef.nativeElement, undefined, { className: 'docx' });
        return;
      }
      alert('Unable to load DOCX file for preview.');
      return;
    }

    // ===== XLS / XLSX / XLSM / CSV =====
    const isSpreadsheet = name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.xlsm') || name.endsWith('.csv');
    if (isSpreadsheet) {
      this.previewType = 'xls';
      this.previewUrl = null;
      try {
        const workbook = await this.attachmentFileService.buildPreviewWorkbook(file, name, getUrl);
        this.workbookCache = workbook;
        this.sheetNames = workbook.SheetNames || [];
        this.selectedSheetIndex = 0;
        this.renderSheetByIndex(this.selectedSheetIndex);
      } catch (err) {
        console.trace('Spreadsheet/CSV preview failed', err);
      }
      return;
    }
    this.previewType = 'unsupported';
  }

  downloadCollectionFile(file: any) {
    const id = file.cd_id;
    if (id) {
      this.createProjectService.readCollectionDocument(id).subscribe((res: any) => {
        this.attachmentFileService.downloadFromResponse(res, file.file_name, DocumentTypeIconComponent.MIME_PDF);
      });
      return;
    }

    console.info('Download not implemented for this file object:', file);
  }

  private _resolveCollectionFileUrl(file: any, id: any, name: string): Promise<string | null> {
    if (!id) {
      return Promise.resolve(file.serverUrl || file.remoteUrl || file.objectUrl || null);
    }
    return this.attachmentFileService
      .resolveFileUrl(this.createProjectService.readCollectionDocument(id), name, url => this._objectUrls.push(url))
      .catch(err => {
        console.trace(this.FAILED_TO_FETCH_FILE, err);
        return null;
      });
  }

  private async _previewCollectionDocx(file: any, getUrl: () => Promise<string | null>): Promise<void> {
    this.previewType = 'docx';
    this.previewUrl = null;
    const arrayBuffer = await this.attachmentFileService.fetchDocxBuffer(file, getUrl);
    if (arrayBuffer) {
      this.docxContainerRef.nativeElement.innerHTML = '';
      await renderAsync(arrayBuffer, this.docxContainerRef.nativeElement, undefined, { className: 'docx' });
      return;
    }
    alert('Unable to load DOCX file for preview.');
  }

  private async _previewCollectionSpreadsheet(file: any, name: string, getUrl: () => Promise<string | null>): Promise<void> {
    this.previewType = 'xls';
    this.previewUrl = null;
    try {
      const workbook = await this.attachmentFileService.buildPreviewWorkbook(file, name, getUrl);
      this.workbookCache = workbook;
      this.sheetNames = workbook.SheetNames || [];
      this.selectedSheetIndex = 0;
      this.renderSheetByIndex(this.selectedSheetIndex);
    } catch (err) {
      console.trace('Spreadsheet/CSV preview failed', err);
    }
  }

  async previewCollectionFile(file: any) {
    if (!file) { return; }

    const name = (file.file_name || '').toLowerCase();
    const id = file.cd_id;
    const getFileUrl = () => this._resolveCollectionFileUrl(file, id, name);

    if (name.endsWith('.pdf')) { await this.attachmentFileService.openPdf(getFileUrl, file, url => this._objectUrls.push(url)); return; }

    const isImage = name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') ||
      (file.type && String(file.type).toLowerCase().startsWith('image/'));
    if (isImage) { await this._previewImage(file, getFileUrl); return; }

    this.previewTitle = file.file_name;
    this.isPreviewOpen = true;
    this._addPreviewHistoryEntry();

    if (name.endsWith('.docx')) { await this._previewCollectionDocx(file, getFileUrl); return; }

    const isSpreadsheet = name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.xlsm') || name.endsWith('.csv');
    if (isSpreadsheet) { await this._previewCollectionSpreadsheet(file, name, getFileUrl); return; }

    this.previewType = 'unsupported';
  }

  onDropFiles(ev: DragEvent) {
    ev.preventDefault();

    // Validate that departments are selected
    if (!this.isUploadEnabled || this.selectedDepartmentIds.length === 0) {
      this.showUploadError('Please select departments before uploading files.');
      return;
    }

    if (!ev.dataTransfer) 
    {
      return;
    }
    const items = ev.dataTransfer.files;
    for (let i = 0; i < items.length; i++) {
      const f = items.item(i);
      if (!f) 
        {
          continue;
        }

      // Enforce allowed content types
      if (!DocumentTypeIconComponent.isAllowedFileType(f)) {
        console.info('Unsupported file type blocked (drag-drop):', f.name, f.type);
        this.showUploadError(`Unsupported file type for "${f.name}". Allowed: PDF, DOCX, XLSX, PNG, JPG, JPEG, CSV.`);
        continue;
      }

      const already = [...this.files, ...this.userUploads].some(x => String(x.name) === f.name && Number(x.size) === f.size);
      if (already) {
        this.showUploadError(`File "${f.name}" already uploaded`);
        continue;
      }
      const objectUrl = URL.createObjectURL(f);
      this._objectUrls.push(objectUrl);

      // Convert file to base64
      this.fileToBase64(f).then(base64 => {
        const uploadPayload: any = {
          projectId: this.projectId,
          projectName: this.projectName,
          departmentIds: this.selectedDepartmentIds,
          fileName: f.name,
          contentType: f.type || DocumentTypeIconComponent.MIME_OCTET_STREAM,
          userName: this.userName,
          fileBuffer: base64,
          uploadedBy: this.userName,
        };

        this.createProjectService.uploadDocument(this.workDayId, uploadPayload).subscribe(
          (res) => {
            console.info("Documents uploaded successfully", res);
            uploadPayload.fileVersioningId = res?.payload?.file_versioning_id;
            uploadPayload.fileName = res?.file_name;

            this.refreshProjectUploads();
          }
        );
      });
    }
    this.selectedCountChange.emit(this.selectedCount);
  }

  private showUploadError(msg?: string) {
    console.info('Upload error:', msg || 'duplicate');

    this.uploadErrorMessage = msg || 'File already exists!';
    this.cdr.detectChanges();
    setTimeout(() => { this.uploadErrorMessage = null; this.cdr.detectChanges(); }, 10000);
  }

  clearUploadError() {
    this.uploadErrorMessage = null;
    this.cdr.detectChanges();
  }

  onDragOver(ev: DragEvent) {
    ev.preventDefault();
  }

  readUploadDocument(u: any) {
    if (!u?.file_versioning_id) {
      console.info("Invalid upload item or missing dfuId", u);
      return;
    }
    console.info("Attempting to delete document:", {
      projectId: this.projectId,
      fileVersioningId: u.file_versioning_id,
      uploadObject: u
    });

    this.createProjectService.readDocument(this.workDayId, u.file_versioning_id).subscribe(
      (res) => {
        console.info("Document removed successfully", res);
      });
  }

  removeUpload(u: any) {

    if (!u?.file_versioning_id) {
      console.trace("Invalid upload item or missing dfuId", u);
      return;
    }
    this.createProjectService.deleteDocument(this.workDayId, u.file_versioning_id).subscribe(
      (res) => {
        console.info("Document removed successfully", res);
        // After deletion, refresh uploads; CopySourceDocumentsComponent reacts to the new
        // userUploads reference to drop any copy-section selection left without an upload.
        this.refreshProjectUploads();
      }
    );

  }

  ngOnDestroy(): void {
    for (const url of this._objectUrls) {
      URL.revokeObjectURL(url);
    }
    this._objectUrls = [];
    // cleanup history listener and state if preview was left open
    if (this._onPopState) {
      globalThis.removeEventListener('popstate', this._onPopState);
      this._onPopState = null;
    }
    if (this._previewHistoryState) {
      // try to remove the pushed history entry
      this._ignoreNextPop = true;
      history.back();
      this._previewHistoryState = false;
    }
  }

  renderSheetByIndex(index: number) {
    try {
      if (!this.workbookCache?.SheetNames || !this.workbookCache?.Sheets) {
        this.excelHtml = '<div class="xlsx-message">No workbook loaded.</div>';
        return;
      }
      const name = this.workbookCache.SheetNames[index];
      if (!name) {
        this.excelHtml = '<div class="xlsx-message">Sheet not found.</div>';
        return;
      }
      const sheet = this.workbookCache.Sheets[name];
      const tableHtml = XLSX.utils.sheet_to_html(sheet);
      const wrapped = `<div class="xlsx-preview">${tableHtml}</div>`;
      this.excelHtml = wrapped;
    } catch (err) {
      console.trace('renderSheetByIndex failed', err);
      this.excelHtml = '<div class="xlsx-message">Unable to render sheet.</div>';
    }
  }

  private _addPreviewHistoryEntry() {
    try {
      if (this._previewHistoryState) 
      {
        return;
      }
      history.pushState({ cert_ui_preview: true }, '');
      this._previewHistoryState = true;
      this._onPopState = (_ev: PopStateEvent) => {
        if (this._ignoreNextPop) { this._ignoreNextPop = false; return; }
        if (this.isPreviewOpen) {
          this.isPreviewOpen = false;
          this.previewType = null;
          this.previewUrl = null;
          this.excelHtml = '';
          // cleanup listener and reset flag
          if (this._onPopState) {
            globalThis.removeEventListener('popstate', this._onPopState);
            this._onPopState = null;
          }
          this._previewHistoryState = false;
        }
      };
      globalThis.addEventListener('popstate', this._onPopState);
    } catch (e) {
      console.info('History push for preview failed', e);
    }
  }

  closePreview(_originatedFromPopstate = false) {
    this.isPreviewOpen = false;
    this.previewType = null;
    this.previewUrl = null;
    this.excelHtml = '';
    if (this._previewHistoryState) {
      if (this._onPopState) {
        globalThis.removeEventListener('popstate', this._onPopState);
        this._onPopState = null;
      }
      this._previewHistoryState = false;
      this._ignoreNextPop = false;
    }
  }

  updateSelectedFilesInService() {
    const selectedFiles = [...this.files, ...this.userUploads].filter((f: any) => f.checked);
    this.createProjectService.setSelectedFiles(selectedFiles);
  }

  refreshProjectUploads(): void {
    if (!this.projectId) {
      return;
    }

    this.createProjectService.documentFetchByProjectId(this.projectId, this.workDayId)
      .subscribe({
        next: (uploads: any[]) => {
          this.userUploads = (uploads || []).map(u => ({
            ...u,
            // Normalize common fields so the template and helpers can use them consistently
            name: u.file_name,
            size: typeof u.size === 'number' ? u.size : (u.file_size || 0),
            date: new Date().toISOString()
          }));
          this.applyUploadedSelectionStateToCollectionFiles();
          // Keep uploaded files state in the service in sync with current UI list
          this.createProjectService.setSelectedFiles(this.userUploads);
          // Optionally emit updated selection count (none selected by default)
          this.selectedCountChange.emit(this.selectedCount);
        },
        error: (err) => {
          console.trace('Failed to fetch uploads by projectId', err);
        }
      });
  }

  private applyUploadedSelectionStateToCollectionFiles(): void {
    if (!this.files?.length) {
      return;
    }

    const uploadedFileNames = new Set(
      (this.userUploads || [])
        .map((u: any) => (u?.file_name || u?.name || '').toLowerCase())
        .filter((name: string) => !!name)
    );

    this.files = this.files.map((f: any) => ({
      ...f,
      checked: uploadedFileNames.has(String(f?.file_name || f?.name || '').toLowerCase())
    }));

    this.selectedCountChange.emit(this.selectedCount);
    this.updateSelectedFilesInService();
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Extract base64 string (remove data URL prefix)
        const result = reader.result as string;
        const base64 = result.split(',')[1]; // Remove 'data:application/pdf;base64,' part
        resolve(base64);
      };
      reader.onerror = () => reject(new Error(reader.error?.message || 'Failed to read file'));
    });
  }

}